import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../../../global/contexts/auth/auth';
import { userKeys } from '../queries/keys';
import { userPaths } from '../queries/paths';

// TSS vite/03 — server state for the `users` entity.
//
// Same shape as the clients feature, with one difference that is not
// cosmetic: the role travels through its own mutation, because the backend
// guards it with its own permission.

export const USERS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// GET /api/v1/admin/users -> { items, pagination: { total, limit, offset } }
// `q` searches name and email; `total` is the total of what matches.
export function useUsersList({ limit = USERS_PAGE_SIZE, offset = 0, q } = {}) {
    const params = { limit, offset };
    if (q) params.q = q;

    return useQuery({
        queryKey: userKeys.list(params),
        queryFn: ({ signal }) => api
            .get(userPaths.list(), { params, signal })
            .then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

// GET /admin/users/{id} does NOT answer the row: it answers the assembled
// UserPackage, permissions already resolved. Which is why `permissions` is
// there to be read and `password_hash` never is.
export function useUser(id) {
    return useQuery({
        queryKey: userKeys.detail(id),
        queryFn: ({ signal }) => api.get(userPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

// POST /admin/users — 201. Requires `users:write`.
export function useCreateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(userPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
    });
}

// PATCH /admin/users/{id} — everything except the role. Requires `users:write`.
export function useUpdateUser(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(userPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: userKeys.detail(id) });
            qc.invalidateQueries({ queryKey: userKeys.all });
        },
    });
}

// PATCH /admin/users/{id}/role — requires `users:admin`.
//
// The generic PATCH also accepts `role` and only asks for `users:write`, so
// the role COULD travel with the rest of the form in a single request. This
// does not do that on purpose: the backend put a stricter permission on
// changing a role, and taking the cheaper door would quietly undo that
// decision from the panel. See the finding reported to mastermind_core.
export function useSetUserRole(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (role) => api.patch(userPaths.role(id), { role }).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: userKeys.detail(id) });
            qc.invalidateQueries({ queryKey: userKeys.all });
        },
    });
}

// The picker's fetcher. It lives here because the endpoint does: a form asking
// for options must not know the URL.
//
// It takes the search term and NOT a page: a picker's job is to find one
// record, and past twenty results the answer is to type more, not to paginate.
export function searchUsers(q, signal) {
    const params = { limit: 20, offset: 0 };
    if (q) params.q = q;
    return api
        .get(userPaths.list(), { params, signal })
        .then(r => r.data.items ?? []);
}

// GET /admin/users/{id}/teams — AD01.
//
// Reemplaza la consulta inversa que el panel hacía antes: una petición por los
// equipos más una por los miembros de cada uno, dada vuelta acá. Aquello veía
// solo la primera página de equipos, así que podía devolver una lista
// incompleta sin decirlo. Esto lee las membresías y ya.
//
// TRES COSAS QUE EL CONTRATO DICE Y LA PANTALLA TIENE QUE RESPETAR:
//
//   · Devuelve las membresías ACTIVAS en equipos ACTIVOS. Un equipo dado de
//     baja no aparece, aunque la persona siga figurando en su fila.
//   · `team_role` es el rol EN ESE EQUIPO, nunca `users.role`. Es descriptivo:
//     solo `lead` concede algo, y lo concede en services/, no por este campo.
//   · `joined_at` es el PRIMER ingreso registrado, no el último. Quitar a
//     alguien desactiva su fila y volver a agregarlo la reactiva conservando la
//     fecha — así que no es «desde cuándo está esta vez».
//
// Pide `users:read` Y `teams:read`. Con uno solo el backend contesta 403, así
// que la pantalla comprueba los dos antes de disparar la petición: un 403 en
// una fila que alguien abrió dispara el aviso global de permisos y le dice que
// hizo algo mal cuando solo miró.
export const USER_TEAMS_PAGE_SIZE = 25;   // el default del backend; tope 100

export function useUserTeams(id, { enabled = true } = {}) {
    const { canAll } = useAuth();
    const allowed = !!id && enabled && canAll('users:read', 'teams:read');
    const params = { limit: USER_TEAMS_PAGE_SIZE, offset: 0 };

    const query = useQuery({
        queryKey: userKeys.teams(id, params),
        queryFn: ({ signal }) => api
            .get(userPaths.teams(id), { params, signal })
            .then(r => r.data),
        enabled: allowed,
        staleTime: 5 * 60_000,
    });

    const items = query.data?.items ?? [];
    const total = query.data?.pagination?.total ?? items.length;

    return {
        items,
        total,
        // `total` cuenta todo antes de paginar, así que esto dice si la página
        // se quedó corta. Alguien en más de 25 equipos es raro y no imposible.
        partial: total > items.length,
        allowed,
        isLoading: query.isLoading,
        error: query.error,
    };
}
